import { useState } from "react";
import useI18n from "@/hooks/useI18n";

interface PlanItem {
  text: string;
  included: boolean;
}

interface PlanRechargeRow {
  recharge: string;
  fee: string;
  balance: string;
}

interface PlanSection {
  id: string;
  title: string;
  items?: PlanItem[];
  description?: string;
  notes?: string[];
  rechargeTable?: PlanRechargeRow[];
  link?: { label: string; href: string };
}

export interface Plan {
  id: string;
  name: string;
  isBestSeller?: boolean;
  badgeLabel?: string;
  promo?: string;
  originalPriceLabel?: string;
  price: string;
  pricePeriod?: string;
  tags?: string[];
  regulationLink?: string;
  ctaLabel: string;
  ctaLink: string;
  summaryHeader?: string;
  summaryFeatures?: string[];
  summaryDisclaimer?: string;
  sections: PlanSection[];
}

const CheckIcon = () => (
  <svg
    className="shrink-0 w-4 h-4 mt-[3px] text-[#00b67a]"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.33 8.33l2.84 2.84 6.5-6.5"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="shrink-0 w-4 h-4 mt-[3px] text-[#d60b52]"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 4l8 8M12 4l-8 8"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FileIcon = () => (
  <svg
    className="shrink-0 w-4 h-4 text-[#0e0d0d]"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.5 1.5h5.25L13 5.75v8.75H3.5z"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
    />
    <path
      d="M8.75 1.5v4.25H13"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinejoin="round"
    />
    <path
      d="M5.5 9h5M5.5 11.25h5"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinecap="round"
    />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M4 6l4 4 4-4"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function buildPlans(t: (key: string) => string): Plan[] {
  const partnershipUrl = "https://www.semparar.com.br/parceria-pedagio-eletronico";
  const praticoRegulationUrl =
    "https://www.semparar.com.br/on/demandware.static/-/Sites-catalog-semparar/default/dwfd40f891/Regulamento_Sem-Parar-Pratico_03-2026.pdf";
  const idealRegulationUrl =
    "https://www.semparar.com.br/on/demandware.static/-/Sites-catalog-semparar/default/dwdeca2c5e/Sem%20Parar%20Ideal.pdf";

  const externalCheckoutBase =
    "http://semparar.com.br/on/demandware.store/Sites-SemParar-Site/default/Cart-ExternalBuyNow";
  const livreCheckoutUrl = `${externalCheckoutBase}?pid=004P-LIVRE-MANUAL-FREEFLOW-PARCERIAS`;
  const praticoCheckoutUrl = `${externalCheckoutBase}?pid=002-ETL-PRATICO-92OFF-10M-FREEFLOW-SIGA-FACIL`;
  const idealCheckoutUrl = `${externalCheckoutBase}?pid=007-ETL-TURBINADO-IDEAL-91OFF-10M-FREEFLOW-SIGA-FACIL`;
  const flexCheckoutUrl = `${externalCheckoutBase}?pid=003P-FLEX-36OFF-FREEFLOW-PARCERIAS`;

  const standardWhereToUse: PlanItem[] = [
    { text: "Pedágios com cancela", included: true },
    { text: "Pedágios eletrônicos (Free Flow)", included: true },
    { text: "Estacionamentos", included: true },
    { text: "Abastecimentos", included: true },
    { text: "Drive-thrus", included: true },
    { text: "Lava-rápidos", included: true },
  ];

  const standardBenefits: PlanItem[] = [
    { text: "Cashback ao abastecer com a Tag", included: true },
    { text: "Um best-seller por mês no Skeelo", included: true },
    { text: "Desconto de 10% na Movida", included: true },
    { text: "Cashback no Sem Parar Shop", included: true },
  ];

  return [
    {
      id: "livre",
      name: "Livre Free Flow",
      isBestSeller: true,
      badgeLabel: t("pages:success:newBadge"),
      promo: "Pedágios eletrônicos",
      originalPriceLabel: "Recarregue e use",
      price: "Sem mensalidade",
      tags: ["Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:iWantThis"),
      ctaLink: livreCheckoutUrl,
      summaryHeader: "Plano gratuito para uso em pedágios eletrônicos (Free Flow).",
      summaryFeatures: [
        "Adesão convertida em recarga",
        "5% de desconto nas tarifas",
      ],
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          description: "Não possui coberturas inclusas",
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: [
            { text: "Pedágios com cancela*", included: true },
            { text: "Pedágios eletrônicos (Free Flow)", included: true },
            { text: "Estacionamentos*", included: true },
            { text: "Abastecimentos*", included: true },
            { text: "Lava-rápidos*", included: true },
            { text: "Drive-thrus*", included: true },
          ],
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        {
          id: "benefits",
          title: t("pages:success:benefits"),
          items: [
            { text: "Cashback ao abastecer com a Tag", included: false },
            { text: "Um best-seller por mês no Skeelo", included: false },
            { text: "Desconto de 10% na Movida", included: true },
            { text: "Cashback no Sem Parar Shop", included: true },
            { text: "Desconto de 5% a 70% na tarifa de pedágios sem cancelas (Free Flow)", included: true },
            { text: "Evite multas! Receba automaticamente a tarifa na fatura Sem Parar", included: true },
          ],
        },
        {
          id: "charge",
          title: t("pages:success:charge"),
          rechargeTable: [
            { recharge: "R$ 50,00", fee: "R$ 0,00", balance: "R$ 50,00" },
            { recharge: "R$ 80,00", fee: "R$ 0,00", balance: "R$ 80,00" },
            { recharge: "R$ 150,00", fee: "R$ 0,00", balance: "R$ 150,00" },
            { recharge: "R$ 200,00", fee: "R$ 0,00", balance: "R$ 200,00" },
            { recharge: "R$ 250,00", fee: "R$ 0,00", balance: "R$ 250,00" },
          ],
        },
        { id: "accession", title: t("pages:success:accession"), description: "R$ 20,00" },
        { id: "maintenance", title: t("pages:success:maintenance"), description: "Sem cobrança" },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description:
            "Este plano é exclusivo para uso em pedágios eletrônicos.\n\n*Caso utilize em pedágios com cancela, estacionamentos, postos, drive-thrus ou lava-rápidos, haverá um acréscimo de R$29,90 na fatura.",
        },
      ],
    },
    {
      id: "pratico",
      name: "Prático",
      promo: "92% OFF por 10 meses",
      originalPriceLabel: "De R$ 49,90 por",
      price: "R$ 2,90",
      pricePeriod: "/até o 10º mês",
      tags: ["Economize R$ 370", "Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:orderTag"),
      ctaLink: praticoCheckoutUrl,
      summaryFeatures: [
        "Pedágio e Free Flow",
        "Estacionamentos",
        "Abastecimentos",
        "Assistência Guincho 24h",
        "Seguro Fatura Protegida",
      ],
      summaryDisclaimer: t("pages:success:noFeeNoFidelity"),
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          items: [
            { text: "Proteções até 40 km", included: true },
            { text: "Socorro mecânico", included: true },
            { text: "Guincho após pane/pane seca", included: true },
            { text: "Guincho após acidente", included: true },
            { text: "Chaveiro automotivo", included: true },
            { text: "Troca de pneus", included: true },
            { text: "Indenização por invalidez permanente por acidente", included: true },
            { text: "Perda de renda por desemprego", included: true },
            { text: "Perda de renda por incapacidade", included: true },
            { text: "Quitação de dívida por morte", included: true },
          ],
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: standardWhereToUse,
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        { id: "benefits", title: t("pages:success:benefits"), items: standardBenefits },
        {
          id: "charge",
          title: t("pages:success:charge"),
          description:
            "O valor com desconto na mensalidade é válido durante a quantidade de meses vigentes na promoção.\n\nApós o término da oferta, o valor de R$49,90/mês volta a ser cobrado.",
        },
        { id: "accession", title: t("pages:success:accession"), description: "Grátis" },
        { id: "maintenance", title: t("pages:success:maintenance"), description: "Sem cobrança" },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description: "Sem cobrança",
        },
        {
          id: "terms",
          title: t("pages:success:regulationTerms"),
          link: {
            label: "Plano Sem Parar Prático",
            href: praticoRegulationUrl,
          },
        },
      ],
    },
    {
      id: "ideal",
      name: "Ideal",
      badgeLabel: t("pages:success:bestSeller"),
      promo: "91% OFF por 10 meses",
      originalPriceLabel: "De R$ 59,90 por",
      price: "R$ 5,39",
      pricePeriod: "/até o 10º mês",
      tags: ["Seguros inclusos", "Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:orderTag"),
      ctaLink: idealCheckoutUrl,
      summaryHeader:
        "Use em pedágios e no Free Flow, ao abastecer, estacionar e mais:",
      summaryFeatures: [
        "Assistência Guincho 24h",
        "Seguro Fatura Protegida",
        "Seguro Acidentes Pessoais",
        "Rastreio veicular e outros",
      ],
      summaryDisclaimer: t("pages:success:noFeeNoFidelity"),
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          items: [
            { text: "Proteções até 40 km", included: true },
            { text: "Socorro mecânico", included: true },
            { text: "Guincho após pane/pane seca", included: true },
            { text: "Guincho após acidente", included: true },
            { text: "Chaveiro automotivo", included: true },
            { text: "Troca de pneus", included: true },
            { text: "Indenização por morte acidental", included: true },
            { text: "Indenização por invalidez permanente por acidente", included: true },
            { text: "Assistência SOS Odonto", included: true },
            { text: "Alerta de débitos", included: true },
            { text: "Perda de renda por desemprego", included: true },
            { text: "Perda de renda por incapacidade", included: true },
            { text: "Quitação de dívida por morte", included: true },
            { text: "Rastreio do veículo por usos", included: true },
            { text: "Acesso aos trajetos do veículo", included: true },
            { text: "Cobertura da rede credenciada", included: true },
            { text: "Serviços de rastreio no SuperApp", included: true },
          ],
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: standardWhereToUse,
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        { id: "benefits", title: t("pages:success:benefits"), items: standardBenefits },
        {
          id: "charge",
          title: t("pages:success:charge"),
          description:
            "O valor com desconto na mensalidade é válido durante a quantidade de meses vigentes na promoção.\n\nApós o término da oferta, o valor de R$59,90/mês volta a ser cobrado.",
        },
        { id: "accession", title: t("pages:success:accession"), description: "Grátis" },
        { id: "maintenance", title: t("pages:success:maintenance"), description: "Sem cobrança" },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description: "Sem cobrança",
        },
        {
          id: "terms",
          title: t("pages:success:regulationTerms"),
          link: {
            label: "Plano Sem Parar Ideal",
            href: idealRegulationUrl,
          },
        },
      ],
    },
    {
      id: "flex",
      name: "Flex",
      promo: "Não usa, não paga!",
      originalPriceLabel: "De R$ 29,90 por",
      price: "R$ 18,90",
      pricePeriod: "/mês que usar",
      tags: ["Frete grátis"],
      regulationLink: partnershipUrl,
      ctaLabel: t("pages:success:iWantThis"),
      ctaLink: flexCheckoutUrl,
      summaryFeatures: [
        "Pedágio e Free Flow",
        "Estacionamentos (2 dias/mês)",
      ],
      sections: [
        {
          id: "coverages",
          title: t("pages:success:coverages"),
          description: "Não possui coberturas inclusas",
        },
        {
          id: "whereToUse",
          title: t("pages:success:whereToUse"),
          items: [
            { text: "Pedágios com cancela", included: true },
            { text: "Pedágios eletrônicos (Free Flow)", included: true },
            { text: "Estacionamentos (2 dias por mês)", included: true },
            { text: "Abastecimentos", included: false },
            { text: "Drive-thrus", included: false },
            { text: "Lava-rápidos", included: false },
          ],
          notes: [
            "Cobertura em todo território brasileiro.",
            "Válido para veículos leves (CONTRAN).",
          ],
        },
        {
          id: "benefits",
          title: t("pages:success:benefits"),
          items: [
            { text: "Cashback ao abastecer com a Tag", included: false },
            { text: "Um best-seller por mês no Skeelo", included: false },
            { text: "Desconto de 10% na Movida", included: true },
            { text: "Cashback no Sem Parar Shop", included: true },
          ],
        },
        {
          id: "charge",
          title: t("pages:success:charge"),
          description:
            "A mensalidade é cobrada somente nos meses que utilizar a tag ao passar no pedágio ou estacionamento.",
        },
        { id: "accession", title: t("pages:success:accession"), description: "R$ 20,00" },
        {
          id: "maintenance",
          title: t("pages:success:maintenance"),
          description:
            "Após 90 dias sem uso, será cobrada manutenção de R$11,90/mês até que a Tag seja utilizada novamente.",
        },
        {
          id: "outOfCoverage",
          title: t("pages:success:outOfCoverage"),
          description:
            "Você terá até 2 dias por mês para usar no estacionamento. A partir do 3º dia de uso, haverá um acréscimo de R$14,00 na mensalidade.",
        },
      ],
    },
  ];
}

const PlanSectionView = ({
  section,
  alternate,
  fillRemaining = false,
}: {
  section: PlanSection;
  alternate: boolean;
  fillRemaining?: boolean;
}) => {
  const bg = alternate ? "bg-[#f9f9f9]" : "bg-white";
  const grow = fillRemaining ? "flex-1 xl:flex-none" : "";
  const baseClass = `${bg} ${grow} border-t border-[#e6e6e6] px-6 py-4 flex flex-col gap-2`;

  if (section.items) {
    return (
      <div className={baseClass}>
        <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
          {section.title}
        </p>
        {section.items.map((item, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            {item.included ? <CheckIcon /> : <CloseIcon />}
            <p className="flex-1 text-sm font-normal text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
              {item.text}
            </p>
          </div>
        ))}
        {section.notes?.map((note, idx) => (
          <p
            key={`note-${idx}`}
            className="text-sm font-normal text-[#0e0d0d] leading-[1.5] tracking-[0.021px] pl-6"
          >
            {note}
          </p>
        ))}
        {section.link && (
          <a
            href={section.link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-normal text-[#355da5] underline leading-[1.5] tracking-[0.021px] mt-1"
          >
            {section.link.label}
          </a>
        )}
      </div>
    );
  }

  if (section.rechargeTable) {
    return (
      <div className={baseClass}>
        <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
          {section.title}
        </p>
        <RechargeTable rows={section.rechargeTable} />
      </div>
    );
  }

  if (!section.description && section.link) {
    return (
      <div className={baseClass}>
        <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
          {section.title}
        </p>
        <a
          href={section.link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm font-normal text-[#355da5] leading-[1.5] tracking-[0.021px]"
        >
          <FileIcon />
          <span>{section.link.label}</span>
        </a>
      </div>
    );
  }

  return (
    <div className={baseClass}>
      <p className="text-sm font-bold text-[#0e0d0d] leading-[1.5] tracking-[0.021px]">
        {section.title}
      </p>
      {section.description?.split("\n\n").map((paragraph, idx) => (
        <p
          key={idx}
          className="text-sm font-normal text-[#0e0d0d] leading-[1.5] tracking-[0.021px]"
        >
          {paragraph}
        </p>
      ))}
    </div>
  );
};

const RechargeTable = ({ rows }: { rows: PlanRechargeRow[] }) => {
  const { t } = useI18n();

  return (
    <div className="flex flex-col text-[12px] text-[#0e0d0d]">
      <div className="grid grid-cols-3 gap-2 pb-1 font-medium">
        <span>{t("pages:success:recharge")}</span>
        <span>{t("pages:success:fee")}</span>
        <span>{t("pages:success:balance")}</span>
      </div>
      {rows.map((row, idx) => (
        <div
          key={idx}
          className={`grid grid-cols-3 gap-2 px-1 py-1 rounded ${
            idx % 2 === 0 ? "bg-[#e6e6e6]" : ""
          }`}
        >
          <span className="font-light">{row.recharge}</span>
          <span className="font-light">{row.fee}</span>
          <span>{row.balance}</span>
        </div>
      ))}
    </div>
  );
};

const PlanCard = ({
  plan,
  isOpen,
  onToggle,
}: {
  plan: Plan;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { t } = useI18n();

  const badgeText = plan.badgeLabel;
  const badgeClassName = plan.isBestSeller
    ? "bg-[#d60b52] text-white text-[12px] font-bold leading-[1.25] tracking-[0.1px] px-3 py-1.5 rounded-md whitespace-nowrap"
    : "bg-[#363636] text-white text-[12px] font-bold leading-[1.25] tracking-[0.1px] px-3 py-1.5 rounded-md whitespace-nowrap";

  const ctaClassName = plan.isBestSeller
    ? "w-full h-12 bg-[#d60b52] hover:opacity-90 text-white font-bold text-sm flex items-center justify-center rounded-lg transition-opacity uppercase"
    : "w-full h-12 bg-white hover:bg-gray-50 text-[#0e0d0d] border border-[#0e0d0d] font-bold text-sm flex items-center justify-center rounded-lg transition-colors uppercase";

  const hasSummary =
    !!plan.summaryHeader ||
    !!plan.summaryDisclaimer ||
    (plan.summaryFeatures && plan.summaryFeatures.length > 0);

  const hasTermsSection = plan.sections.some((s) => s.id === "terms");
  const rowSpanClass = hasTermsSection ? "xl:row-span-13" : "xl:row-span-12";

  return (
    <div
      className={`relative min-w-[280px] max-w-[280px] xl:max-w-none bg-white rounded-[16px] overflow-hidden flex-shrink-0 snap-center self-stretch flex flex-col xl:flex-shrink ${rowSpanClass} xl:grid xl:grid-rows-subgrid border border-[#e6e6e6]`}
    >
      {plan.isBestSeller && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-[16px] z-20"
          style={{
            padding: "1.5px",
            background:
              "linear-gradient(to bottom, #d60b52 0%, rgba(214, 11, 82, 0.7) 25%, rgba(214, 11, 82, 0.35) 55%, rgba(214, 11, 82, 0.1) 85%, transparent 100%)",
            WebkitMask:
              "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "exclude",
          }}
        />
      )}
      <div className="bg-white px-6 h-[50px] flex items-center gap-2">
        <p
          className={`flex-1 text-[18px] leading-[1.3] text-[#0e0d0d] ${
            plan.isBestSeller ? "font-extrabold" : "font-semibold"
          }`}
        >
          {plan.name}
        </p>
        {badgeText && <span className={badgeClassName}>{badgeText}</span>}
      </div>

      <div className="border-t border-[#e6e6e6] bg-white px-5 py-4 flex flex-col gap-3">
        {plan.promo && (
          <p className="text-[16px] font-semibold text-[#0e0d0d] leading-[1.3]">
            {plan.promo}
          </p>
        )}
        <div className="flex flex-col">
          {plan.originalPriceLabel && (
            <p className="text-[12px] font-normal text-[#696966] leading-[1.3] tracking-[0.018px]">
              {plan.originalPriceLabel}
            </p>
          )}
          <p className="text-[#0e0d0d]">
            <span className="text-[24px] font-extrabold leading-[1.2] tracking-[-0.2px]">
              {plan.price}
            </span>
            {plan.pricePeriod && (
              <span className="text-[12px] font-normal leading-[1.3] tracking-[0.018px]">
                {plan.pricePeriod}
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 min-h-[28px]">
          {plan.tags?.map((tag, index) => (
            <span
              key={index}
              className="bg-[#363636] text-white text-[12px] font-bold leading-[1.25] tracking-[0.1px] px-3 py-1.5 rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {hasSummary && (
        <div className="bg-white px-5 py-4 flex flex-col gap-3">
          {plan.summaryHeader && (
            <p className="text-sm text-[#0e0d0d] leading-[1.4]">
              {plan.summaryHeader}
            </p>
          )}
          {plan.summaryFeatures && plan.summaryFeatures.length > 0 && (
            <ul className="flex flex-col gap-2">
              {plan.summaryFeatures.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckIcon />
                  <span className="text-sm text-[#0e0d0d] leading-[1.4]">
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {plan.summaryDisclaimer && (
            <p className="text-xs text-[#696966] leading-[1.4] mt-2">
              {plan.summaryDisclaimer}
            </p>
          )}
        </div>
      )}

      <div className="bg-white px-5 py-4 mt-auto">
        <a
          href={plan.ctaLink}
          target="_blank"
          rel="noopener noreferrer"
          className={ctaClassName}
        >
          {plan.ctaLabel}
        </a>
      </div>

      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-between gap-2 px-5 py-3 border-t border-[#e6e6e6] bg-white text-[#696966] text-sm leading-[1.5] tracking-[0.021px] hover:text-[#0e0d0d] transition-colors"
        aria-expanded={isOpen}
        aria-controls={`plan-${plan.id}-details`}
      >
        <span>
          {isOpen
            ? t("pages:success:hidePlanDetails")
            : t("pages:success:viewPlanDetails")}
        </span>
        <ChevronIcon open={isOpen} />
      </button>

      {isOpen &&
        plan.sections.map((section, index) => (
          <PlanSectionView
            key={section.id}
            section={section}
            alternate={index % 2 === 1}
          />
        ))}
    </div>
  );
};

export default function PlanSelection() {
  const { t } = useI18n();
  const plans = buildPlans(t);
  const [areDetailsOpen, setAreDetailsOpen] = useState(false);

  const toggleDetails = () => setAreDetailsOpen((v) => !v);

  return (
    <div className="w-full">
      <div className="text-left md:text-center mb-6 px-2 md:px-0">
        <p
          className="text-base text-[#0e0d0d] font-medium leading-[1.3] tracking-[-0.4px]"
          style={{ fontFamily: "Ubuntu, sans-serif" }}
        >
          {t("pages:success:choosePlan")}
          <span className="text-[#d60b52]">{` tag Sem Parar`}</span>
          {t("pages:success:choosePlanSuffix")}
        </p>
      </div>

      <div
        className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory items-stretch xl:overflow-x-visible xl:-mx-0 xl:px-0 xl:snap-none xl:grid xl:justify-center xl:grid-cols-[repeat(4,minmax(280px,320px))] xl:gap-x-4 xl:gap-y-0"
        style={{
          gridTemplateRows:
            "repeat(13, auto)" /* header + miolo + summary + CTA + toggle + 7 sections + terms (Prático/Ideal) */,
        }}
      >
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isOpen={areDetailsOpen}
            onToggle={toggleDetails}
          />
        ))}
      </div>
    </div>
  );
}
